import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, css } from "aphrodite";
import { string, func, bool } from "prop-types";
import { DatePicker, Popover, Typography, Calendar, Select, Button, Radio } from "antd";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const customDatePickerDateFormat = "MMM DD, YYYY";
export const customDatePickerApiDateFormat = "YYYY-MM-DD";

const customDatePickerStyleSheet = StyleSheet.create({
	popoverContentWrapper: {
		display: "flex",
		flexDirection: "column",
		maxWidth: "777px",
		position: "relative",
		margin: "-12px",
	},
	popoverCalendarsWrapper: {
		display: "flex",
		flexDirection: "row",
		gap: "1px",
		backgroundColor: "#F0F0F0",
	},
	dropdownWrapper: {
		border: "none",
	},
	predefinedDatesWrapper: {
		display: "flex",
		flexDirection: "column",
		padding: "16px",
		gap: "8px",
		backgroundColor: "white",
		width: "fit-content",
		whiteSpace: "nowrap",
	},
	radioButton: {
		marginInlineEnd: "unset",
	},
	popoverFooterWrapper: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-end",
		padding: "9px 12px",
		borderTop: "1px solid #F0F0F0",
	},
	calendarDatePickerWrapper: {
		display: "flex",
		flexDirection: "column",
		width: "350px",
		padding: "16px 20px",
		backgroundColor: "white",
		position: "relative",
	},
	errorText: {
		position: "absolute",
		top: "45px",
		left: "20px",
	},
	calendarHeaderWrapper: {
		display: "flex",
		flexDirection: "row",
		width: "100%",
		padding: "0 8px",
		justifyContent: "space-between",
		alignItems: "center",
	},
	calendarHeaderDropdownsWrapper: {
		display: "flex",
		flexDirection: "row",
		width: "fit-content",
		padding: "10px 0",
	},
	arrowButton: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "0",
		width: "fit-content",
		background: "transparent",
		border: "none",
		boxShadow: "none",
	},
});

export const customDatePickerProps = {
	startDate: string,
	endDate: string,
	minimumDate: string,
	onChange: func,
	disabled: bool,
};

const CustomDatePicker = ({
	minimumDate = "01-01-2000",
	startDate,
	endDate,
	onChange,
	disabled,
}) => {
	const popoverContentWrapperRef = useRef(null);
	const applyChangesButtonRef = useRef(null);
	const [popoverIsOpen, setPopoverIsOpen] = useState(false);

	const radioButtonGroupRef = useRef(null);
	const [checkedRadioButtonValue, setCheckedRadioButtonValue] = useState(null);
	const [currentSelectedCheckedRadioButtonValue, setCurrentSelectedCheckedRadioButtonValue] =
		useState(checkedRadioButtonValue);
	const onRadioButtonChange = (e) => {
		setCurrentSelectedCheckedRadioButtonValue(e.target.value);
	};

	const startDatePickerRef = useRef(null);
	const [startErrorMessage, setStartErrorMessage] = useState(null);
	const [currentStartDate, setCurrentStartDate] = useState(startDate ? dayjs(startDate) : null);
	const [currentSelectedStartDate, setCurrentSelectedStartDate] = useState(currentStartDate);

	const endDatePickerRef = useRef(null);
	const [endErrorMessage, setEndErrorMessage] = useState(null);
	const [currentEndDate, setCurrentEndDate] = useState(endDate ? dayjs(endDate) : null);
	const [currentSelectedEndDate, setCurrentSelectedEndDate] = useState(currentEndDate);

	const months = dayjs.months();
	const currentYear = dayjs().year();
	const oldestYear = dayjs(minimumDate).year();
	const years = Array.from(
		{ length: currentYear + 10 - oldestYear + 1 },
		(_, index) => oldestYear + index,
	);

	const onMonthChange = (value, currentDate, setDate) => {
		const newDate = (currentDate || dayjs()).month(months?.indexOf(value));
		setDate(newDate);
	};

	const onYearChange = (value, currentDate, setDate) => {
		const newDate = (currentDate || dayjs()).year(value);
		setDate(newDate);
	};

	const handleKeyDown = (event) => {
		const radioButtons = radioButtonGroupRef.current.querySelectorAll('input[type="radio"]');
		let forwardFocusIndex = -1;
		let backWardFocusIndex = radioButtons.length - 1;

		radioButtons.forEach((button, index) => {
			if (button === document.activeElement) {
				forwardFocusIndex = index;
				backWardFocusIndex = index;
			}
		});

		if (event.shiftKey && event.key === "Tab") {
			event.preventDefault();
			if (document.activeElement.type === "radio") {
				if (backWardFocusIndex > 0) {
					radioButtons[backWardFocusIndex - 1].focus();
				} else {
					if (applyChangesButtonRef.current) {
						applyChangesButtonRef.current.focus();
					}
				}
			}
			if (document.activeElement.id === "startDatePicker") {
				backWardFocusIndex = radioButtons.length - 1;
				radioButtons[backWardFocusIndex].focus();
			}
			if (document.activeElement.id === "endDatePicker") {
				if (startDatePickerRef.current) {
					startDatePickerRef.current.focus();
				}
			}
		}

		if (!event.shiftKey && event.key === "Tab") {
			event.preventDefault();

			if (document.activeElement.id === "endDatePicker") {
				if (applyChangesButtonRef.current) {
					applyChangesButtonRef.current.focus();
				}
			}
			if (document.activeElement.id === "startDatePicker") {
				if (endDatePickerRef.current) {
					endDatePickerRef.current.focus();
				}
			}
			if (document.activeElement.id === "startDatePicker") {
				if (endDatePickerRef.current) {
					endDatePickerRef.current.focus();
				}
			}
			if (document.activeElement.type === "radio") {
				if (forwardFocusIndex < radioButtons.length - 1) {
					radioButtons[forwardFocusIndex + 1].focus();
				} else {
					if (startDatePickerRef.current) {
						startDatePickerRef.current.focus();
					}
				}
			}
		}

		if (document.activeElement.type === "radio") {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				if (forwardFocusIndex < radioButtons.length - 1) {
					radioButtons[forwardFocusIndex + 1].focus();
				} else {
					radioButtons[0].focus();
				}
			}

			if (event.key === "ArrowUp") {
				event.preventDefault();
				if (forwardFocusIndex > 0) {
					radioButtons[forwardFocusIndex - 1].focus();
				} else {
					radioButtons[radioButtons.length - 1].focus();
				}
			}
		}

		if (event.key === "Enter") {
			if (document.activeElement.id === "endDatePicker") {
				event.preventDefault();

				if (applyChangesButtonRef.current) {
					applyChangesButtonRef.current.focus();
				}
			}
			if (document.activeElement.id === "startDatePicker") {
				event.preventDefault();

				if (endDatePickerRef.current) {
					endDatePickerRef.current.focus();
				}
			}

			if (document.activeElement.type === "radio") {
				event.preventDefault();

				document.activeElement.click();

				if (startDatePickerRef.current) {
					startDatePickerRef.current.focus();
				}
			}
		}
	};

	useEffect(() => {
		let calendarStartDate = null;
		const calendarEndDate = dayjs();

		switch (currentSelectedCheckedRadioButtonValue) {
			case "Last 7 Days":
				calendarStartDate = calendarEndDate.subtract(7, "day");
				break;
			case "Last 14 Days":
				calendarStartDate = calendarEndDate.subtract(14, "day");
				break;
			case "Last 30 Days":
				calendarStartDate = calendarEndDate.subtract(30, "day");
				break;
			case "Last 90 Days":
				calendarStartDate = calendarEndDate.subtract(90, "day");
				break;
			case "Last 365 Days":
				calendarStartDate = calendarEndDate.subtract(365, "day");
				break;
			case "Year to Date":
				calendarStartDate = dayjs().startOf("year");
				break;
			default:
				calendarStartDate = null;
		}
		if (currentSelectedCheckedRadioButtonValue !== null && calendarStartDate !== null) {
			setCurrentSelectedStartDate(calendarStartDate);
			setCurrentSelectedEndDate(calendarEndDate);
		}
	}, [currentSelectedCheckedRadioButtonValue]);

	useEffect(() => {
		const popoverContent = popoverContentWrapperRef.current;
		if (popoverContent && popoverIsOpen) {
			popoverContent.addEventListener("keydown", handleKeyDown);
		}
		return () => {
			if (popoverContent) {
				popoverContent.removeEventListener("keydown", handleKeyDown);
			}
		};
	}, [popoverIsOpen]);

	useEffect(() => {
		if (currentSelectedStartDate?.isBefore(dayjs(minimumDate))) {
			setEndErrorMessage(`Start date can't be before ${minimumDate}`);
		} else if (
			!!currentSelectedStartDate
				? currentSelectedStartDate?.isAfter(currentSelectedEndDate || dayjs())
				: false
		) {
			setEndErrorMessage("Start date can't be after end date");
		} else {
			setStartErrorMessage(null);
		}

		if (currentSelectedEndDate?.isBefore(dayjs(minimumDate))) {
			setEndErrorMessage(`End date can't be before ${minimumDate}`);
		} else if (
			!!currentSelectedStartDate
				? currentSelectedEndDate?.isBefore(currentSelectedStartDate)
				: false
		) {
			setEndErrorMessage("End date can't be before start date");
		} else {
			setEndErrorMessage(null);
		}
	}, [currentSelectedStartDate, currentSelectedEndDate]);

	useEffect(() => {
		setCurrentStartDate(startDate ? dayjs(startDate) : null);
		setCurrentEndDate(endDate ? dayjs(endDate) : null);
	}, [startDate, endDate]);

	return (
		<Popover
			open={popoverIsOpen && !disabled}
			content={
				<div
					ref={popoverContentWrapperRef}
					className={css(customDatePickerStyleSheet.popoverContentWrapper)}
				>
					<div className={css(customDatePickerStyleSheet.popoverCalendarsWrapper)}>
						<Radio.Group
							ref={radioButtonGroupRef}
							onChange={onRadioButtonChange}
							value={currentSelectedCheckedRadioButtonValue}
							className={css(customDatePickerStyleSheet.predefinedDatesWrapper)}
						>
							<Radio
								className={css(customDatePickerStyleSheet.radioButton)}
								value={"Last 7 Days"}
							>
								Last 7 Days
							</Radio>
							<Radio
								className={css(customDatePickerStyleSheet.radioButton)}
								value={"Last 14 Days"}
							>
								Last 14 Days
							</Radio>
							<Radio
								className={css(customDatePickerStyleSheet.radioButton)}
								value={"Last 30 Days"}
							>
								Last 30 Days
							</Radio>
							<Radio
								className={css(customDatePickerStyleSheet.radioButton)}
								value={"Last 90 Days"}
							>
								Last 90 Days
							</Radio>
							<Radio
								className={css(customDatePickerStyleSheet.radioButton)}
								value={"Last 365 Days"}
							>
								Last 365 Days
							</Radio>
							<Radio
								className={css(customDatePickerStyleSheet.radioButton)}
								value={"Year to Date"}
							>
								Year to Date
							</Radio>
						</Radio.Group>
						<div className={css(customDatePickerStyleSheet.calendarDatePickerWrapper)}>
							{!!startErrorMessage && (
								<Text
									className={css(customDatePickerStyleSheet.errorText)}
									type="danger"
								>
									{startErrorMessage}
								</Text>
							)}
							<DatePicker
								status={!!startErrorMessage ? "error" : null}
								format={customDatePickerDateFormat}
								allowEmpty={true}
								open={false}
								placeholder={"Start date"}
								value={currentSelectedStartDate}
								ref={startDatePickerRef}
								id="startDatePicker"
								onBlur={(e) => {
									if (!!e.target.value && !!currentSelectedStartDate) {
										if (
											dayjs(e.target.value).format(
												customDatePickerDateFormat,
											) !==
											currentSelectedStartDate.format(
												customDatePickerDateFormat,
											)
										) {
											setCurrentSelectedCheckedRadioButtonValue(null);
										}
									}
									if (
										dayjs(e.target.value).format(customDatePickerDateFormat) ===
											"Invalid Date" &&
										!!e.target.value
									) {
										setStartErrorMessage("Invalid Date");
									} else {
										if (!e.target.value) {
											setCurrentSelectedStartDate(null);
											setStartErrorMessage(null);
										} else {
											setCurrentSelectedStartDate(dayjs(e.target.value));
											setStartErrorMessage(null);
										}
									}
								}}
								onChange={(value) => {
									setCurrentSelectedStartDate(value);
									setCurrentSelectedCheckedRadioButtonValue(null);
								}}
								allowClear={true}
							/>
							<Calendar
								validRange={[dayjs(minimumDate), currentSelectedEndDate]}
								headerRender={() => (
									<div
										className={css(
											customDatePickerStyleSheet.calendarHeaderWrapper,
										)}
									>
										<Button
											className={css(customDatePickerStyleSheet.arrowButton)}
											icon={
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="22"
													height="40"
													viewBox="0 0 22 40"
													fill="none"
												>
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M12.0607 16.0607L11 15L6 20L11 25L12.0607 23.9393L8.12132 20L12.0607 16.0607Z"
														fill="black"
														fillOpacity="0.25"
													/>
												</svg>
											}
											onClick={() => {
												setCurrentSelectedStartDate(
													(currentSelectedStartDate || dayjs())?.subtract(
														1,
														"month",
													),
												);
											}}
											disabled={currentSelectedStartDate?.isSameOrBefore(
												dayjs(minimumDate),
												"month",
											)}
										/>

										<div
											className={css(
												customDatePickerStyleSheet.calendarHeaderDropdownsWrapper,
											)}
										>
											<Select
												variant="borderless"
												suffixIcon={
													<svg
														style={{
															marginTop: "auto",
														}}
														xmlns="http://www.w3.org/2000/svg"
														width="10"
														height="8"
														viewBox="0 0 10 8"
														fill="none"
													>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z"
															fill="black"
															fillOpacity="0.25"
														/>
													</svg>
												}
												value={
													months[
														(
															currentSelectedStartDate || dayjs()
														)?.month()
													]
												}
												onChange={(value) => {
													onMonthChange(
														value,
														currentSelectedStartDate,
														setCurrentSelectedStartDate,
													);
													setCurrentSelectedCheckedRadioButtonValue(null);
												}}
												dropdownStyle={{ width: "105px" }}
											>
												{months?.map((month) => (
													<Option key={month} value={month}>
														{month}
													</Option>
												))}
											</Select>
											<Select
												key={currentSelectedStartDate}
												variant="borderless"
												suffixIcon={
													<svg
														style={{
															marginTop: "auto",
														}}
														xmlns="http://www.w3.org/2000/svg"
														width="10"
														height="8"
														viewBox="0 0 10 8"
														fill="none"
													>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z"
															fill="black"
															fillOpacity="0.25"
														/>
													</svg>
												}
												value={(
													currentSelectedStartDate || dayjs()
												)?.year()}
												onChange={(value) => {
													onYearChange(
														value,
														currentSelectedStartDate,
														setCurrentSelectedStartDate,
													);
													setCurrentSelectedCheckedRadioButtonValue(null);
												}}
											>
												{years?.map((year) => (
													<Option key={year} value={year}>
														{year}
													</Option>
												))}
											</Select>
										</div>
										<Button
											className={css(customDatePickerStyleSheet.arrowButton)}
											icon={
												<svg
													className={css(
														customDatePickerStyleSheet.arrowButton,
													)}
													xmlns="http://www.w3.org/2000/svg"
													width="22"
													height="40"
													viewBox="0 0 22 40"
													fill="none"
												>
													<mask
														id="path-1-inside-1_563_11577"
														fill="white"
													>
														<path d="M11 15L16 20L11 25L9.93934 23.9393L13.8787 20L9.93934 16.0607L11 15Z" />
													</mask>
													<path
														d="M11 15L16 20L11 25L9.93934 23.9393L13.8787 20L9.93934 16.0607L11 15Z"
														fill="white"
													/>
													<path
														d="M16 20L17.0607 21.0607L18.1213 20L17.0607 18.9393L16 20ZM11 15L12.0607 13.9393L11 12.8787L9.93934 13.9393L11 15ZM11 25L9.93934 26.0607L11 27.1213L12.0607 26.0607L11 25ZM9.93934 16.0607L8.87868 15L7.81802 16.0607L8.87868 17.1213L9.93934 16.0607ZM9.93934 23.9393L8.87868 22.8787L7.81802 23.9393L8.87868 25L9.93934 23.9393ZM13.8787 20L14.9393 21.0607L16 20L14.9393 18.9393L13.8787 20ZM17.0607 18.9393L12.0607 13.9393L9.93934 16.0607L14.9393 21.0607L17.0607 18.9393ZM12.0607 26.0607L17.0607 21.0607L14.9393 18.9393L9.93934 23.9393L12.0607 26.0607ZM9.93934 13.9393L8.87868 15L11 17.1213L12.0607 16.0607L9.93934 13.9393ZM8.87868 25L9.93934 26.0607L12.0607 23.9393L11 22.8787L8.87868 25ZM12.818 18.9393L8.87868 22.8787L11 25L14.9393 21.0607L12.818 18.9393ZM8.87868 17.1213L12.818 21.0607L14.9393 18.9393L11 15L8.87868 17.1213Z"
														fill="black"
														fillOpacity="0.25"
														mask="url(#path-1-inside-1_563_11577)"
													/>
												</svg>
											}
											onClick={() => {
												setCurrentSelectedStartDate(
													currentSelectedStartDate?.add(1, "month"),
												);
											}}
											disabled={(
												currentSelectedStartDate || dayjs()
											)?.isSameOrAfter(dayjs(), "month")}
										/>
									</div>
								)}
								fullscreen={false}
								onSelect={(date) => {
									setCurrentSelectedStartDate(date);
									if (startDatePickerRef.current) {
										startDatePickerRef.current.focus();
									}
									if (
										!!currentSelectedStartDate &&
										!date.isSame(currentSelectedStartDate)
									) {
										setCurrentSelectedCheckedRadioButtonValue(null);
									}
								}}
								value={currentSelectedStartDate}
								onChange={(date) => {
									setCurrentSelectedStartDate(date);
								}}
								defaultValue={null}
							/>
						</div>
						<div className={css(customDatePickerStyleSheet.calendarDatePickerWrapper)}>
							{!!endErrorMessage && (
								<Text
									className={css(customDatePickerStyleSheet.errorText)}
									type="danger"
								>
									{endErrorMessage}
								</Text>
							)}
							<DatePicker
								status={!!endErrorMessage ? "error" : null}
								format={customDatePickerDateFormat}
								allowEmpty={true}
								open={false}
								placeholder={"End date"}
								value={currentSelectedEndDate}
								ref={endDatePickerRef}
								id="endDatePicker"
								onBlur={(e) => {
									if (!!e.target.value && !!currentSelectedEndDate) {
										if (
											dayjs(e.target.value).format(
												customDatePickerDateFormat,
											) !==
											currentSelectedEndDate.format(
												customDatePickerDateFormat,
											)
										) {
											setCurrentSelectedCheckedRadioButtonValue(null);
										}
									}
									if (
										dayjs(e.target.value).format(customDatePickerDateFormat) ===
											"Invalid Date" &&
										!!e.target.value
									) {
										setEndErrorMessage("Invalid Date");
									} else {
										if (!e.target.value) {
											setCurrentSelectedEndDate(null);
											setEndErrorMessage(null);
										} else {
											setCurrentSelectedEndDate(dayjs(e.target.value));
											setEndErrorMessage(null);
										}
									}
								}}
								onChange={(value) => {
									setCurrentSelectedEndDate(value);
									setCurrentSelectedCheckedRadioButtonValue(null);
								}}
								allowClear={true}
							/>
							<Calendar
								validRange={[currentSelectedStartDate || dayjs(minimumDate), null]}
								headerRender={() => (
									<div
										className={css(
											customDatePickerStyleSheet.calendarHeaderWrapper,
										)}
									>
										<Button
											className={css(customDatePickerStyleSheet.arrowButton)}
											icon={
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="22"
													height="40"
													viewBox="0 0 22 40"
													fill="none"
												>
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M12.0607 16.0607L11 15L6 20L11 25L12.0607 23.9393L8.12132 20L12.0607 16.0607Z"
														fill="black"
														fillOpacity="0.25"
													/>
												</svg>
											}
											onClick={() => {
												setCurrentSelectedEndDate(
													(currentSelectedEndDate || dayjs())?.subtract(
														1,
														"month",
													),
												);
											}}
											disabled={currentSelectedEndDate?.isSameOrBefore(
												dayjs(minimumDate),
												"month",
											)}
										/>

										<div
											className={css(
												customDatePickerStyleSheet.calendarHeaderDropdownsWrapper,
											)}
										>
											<Select
												variant="borderless"
												suffixIcon={
													<svg
														style={{
															marginTop: "auto",
														}}
														xmlns="http://www.w3.org/2000/svg"
														width="10"
														height="8"
														viewBox="0 0 10 8"
														fill="none"
													>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z"
															fill="black"
															fillOpacity="0.25"
														/>
													</svg>
												}
												value={
													months[
														(currentSelectedEndDate || dayjs())?.month()
													]
												}
												onChange={(value) => {
													onMonthChange(
														value,
														currentSelectedEndDate,
														setCurrentSelectedEndDate,
													);
													setCurrentSelectedCheckedRadioButtonValue(null);
												}}
												dropdownStyle={{ width: "105px" }}
											>
												{months?.map((month) => (
													<Option key={month} value={month}>
														{month}
													</Option>
												))}
											</Select>
											<Select
												key={currentSelectedEndDate}
												variant="borderless"
												suffixIcon={
													<svg
														style={{
															marginTop: "auto",
														}}
														xmlns="http://www.w3.org/2000/svg"
														width="10"
														height="8"
														viewBox="0 0 10 8"
														fill="none"
													>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z"
															fill="black"
															fillOpacity="0.25"
														/>
													</svg>
												}
												value={(currentSelectedEndDate || dayjs())?.year()}
												onChange={(value) => {
													onYearChange(
														value,
														currentSelectedEndDate,
														setCurrentSelectedEndDate,
													);
													setCurrentSelectedCheckedRadioButtonValue(null);
												}}
											>
												{years?.map((year) => (
													<Option key={year} value={year}>
														{year}
													</Option>
												))}
											</Select>
										</div>
										<Button
											className={css(customDatePickerStyleSheet.arrowButton)}
											icon={
												<svg
													className={css(
														customDatePickerStyleSheet.arrowButton,
													)}
													xmlns="http://www.w3.org/2000/svg"
													width="22"
													height="40"
													viewBox="0 0 22 40"
													fill="none"
												>
													<mask
														id="path-1-inside-1_563_11577"
														fill="white"
													>
														<path d="M11 15L16 20L11 25L9.93934 23.9393L13.8787 20L9.93934 16.0607L11 15Z" />
													</mask>
													<path
														d="M11 15L16 20L11 25L9.93934 23.9393L13.8787 20L9.93934 16.0607L11 15Z"
														fill="white"
													/>
													<path
														d="M16 20L17.0607 21.0607L18.1213 20L17.0607 18.9393L16 20ZM11 15L12.0607 13.9393L11 12.8787L9.93934 13.9393L11 15ZM11 25L9.93934 26.0607L11 27.1213L12.0607 26.0607L11 25ZM9.93934 16.0607L8.87868 15L7.81802 16.0607L8.87868 17.1213L9.93934 16.0607ZM9.93934 23.9393L8.87868 22.8787L7.81802 23.9393L8.87868 25L9.93934 23.9393ZM13.8787 20L14.9393 21.0607L16 20L14.9393 18.9393L13.8787 20ZM17.0607 18.9393L12.0607 13.9393L9.93934 16.0607L14.9393 21.0607L17.0607 18.9393ZM12.0607 26.0607L17.0607 21.0607L14.9393 18.9393L9.93934 23.9393L12.0607 26.0607ZM9.93934 13.9393L8.87868 15L11 17.1213L12.0607 16.0607L9.93934 13.9393ZM8.87868 25L9.93934 26.0607L12.0607 23.9393L11 22.8787L8.87868 25ZM12.818 18.9393L8.87868 22.8787L11 25L14.9393 21.0607L12.818 18.9393ZM8.87868 17.1213L12.818 21.0607L14.9393 18.9393L11 15L8.87868 17.1213Z"
														fill="black"
														fillOpacity="0.25"
														mask="url(#path-1-inside-1_563_11577)"
													/>
												</svg>
											}
											onClick={() => {
												setCurrentSelectedEndDate(
													currentSelectedEndDate?.add(1, "month"),
												);
											}}
											disabled={(
												currentSelectedEndDate || dayjs()
											)?.isSameOrAfter(dayjs(), "month")}
										/>
									</div>
								)}
								fullscreen={false}
								onSelect={(date) => {
									setCurrentSelectedEndDate(date);
									if (endDatePickerRef.current) {
										endDatePickerRef.current.focus();
									}
									if (
										!!currentSelectedEndDate &&
										!date.isSame(currentSelectedEndDate)
									) {
										setCurrentSelectedCheckedRadioButtonValue(null);
									}
								}}
								value={currentSelectedEndDate}
								onChange={(date) => {
									setCurrentSelectedEndDate(date);
								}}
								defaultValue={null}
							/>
						</div>
					</div>
					<div className={css(customDatePickerStyleSheet.popoverFooterWrapper)}>
						<Button
							disabled={
								currentSelectedStartDate?.isBefore(dayjs(minimumDate)) ||
								(!!currentSelectedStartDate
									? currentSelectedStartDate?.isAfter(currentSelectedEndDate)
									: false) ||
								(!!currentSelectedStartDate
									? currentSelectedEndDate?.isBefore(currentSelectedStartDate)
									: false)
							}
							ref={applyChangesButtonRef}
							id="applyChangesButton"
							onClick={() => {
								setPopoverIsOpen(false);
								setCurrentStartDate(currentSelectedStartDate);
								setCurrentEndDate(currentSelectedEndDate);
								setCheckedRadioButtonValue(currentSelectedCheckedRadioButtonValue);
								if (onChange) {
									onChange(
										[
											currentSelectedStartDate
												? currentSelectedStartDate.format(
														customDatePickerApiDateFormat,
													)
												: null,
											currentSelectedEndDate
												? currentSelectedEndDate.format(
														customDatePickerApiDateFormat,
													)
												: null,
										],
										[currentSelectedStartDate, currentSelectedEndDate],
									);
								}
							}}
							type="primary"
							onKeyDown={(event) => {
								if (document.activeElement.id === "applyChangesButton") {
									if (!event.shiftKey && event.key === "Tab") {
										event.preventDefault();
										const radioButtons =
											radioButtonGroupRef.current.querySelectorAll(
												'input[type="radio"]',
											);
										if (radioButtons.length > 0) {
											radioButtons[0].focus();
										}
									}
									if (event.shiftKey && event.key === "Tab") {
										event.preventDefault();
										if (endDatePickerRef.current) {
											endDatePickerRef.current.focus();
										}
									}
								}
							}}
						>
							{"Apply"}
						</Button>
					</div>
				</div>
			}
			trigger="click"
			placement="bottomLeft"
			onOpenChange={(newOpen) => {
				setPopoverIsOpen(newOpen);
			}}
		>
			<RangePicker
				format={customDatePickerDateFormat}
				disabled={disabled}
				open={false}
				allowEmpty={[true, true]}
				value={[currentStartDate, currentEndDate]}
				onFocus={(event, { range }) => {
					setStartErrorMessage(null);
					setEndErrorMessage(null);
					setCurrentSelectedCheckedRadioButtonValue(checkedRadioButtonValue);
					setCurrentSelectedStartDate(currentStartDate);
					setCurrentSelectedEndDate(currentEndDate);
					if (range === "start") {
						setTimeout(() => {
							if (startDatePickerRef.current) {
								startDatePickerRef.current.focus();
							}
						}, 500);
					}
					if (range === "end") {
						setTimeout(() => {
							if (endDatePickerRef.current) {
								endDatePickerRef.current.focus();
							}
						}, 500);
					}
					setTimeout(() => setPopoverIsOpen(true), 300);
				}}
				onChange={(dates) => {
					if (dates === null || dates.length === 0) {
						setCurrentStartDate(null);
						setCurrentEndDate(null);
						setCurrentSelectedStartDate(null);
						setCurrentSelectedEndDate(null);
						setCurrentSelectedCheckedRadioButtonValue(null);
						setCheckedRadioButtonValue(null);
						if (onChange) {
							onChange([null, null], null);
						}
					}
				}}
			/>
		</Popover>
	);
};

CustomDatePicker.propTypes = customDatePickerProps;

export default CustomDatePicker;
